import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import TableOrganism from "../../organisms/Table/TableOrganism";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import Loader from "../../atoms/Loader/LoaderAtom";
import { useLoaderDispatch, useLoaderState } from "../../context/LoaderContext";
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import { useLocation, useParams } from "react-router-dom";
import { createReportsRows } from "./CreateReportsTable";
import { CallToActionButton } from "../../atoms/callToActionButton/CallToActionButton";
import { largeButton } from "../../utilities/style";
import useApiService from "../../utilities/useApiService";
import CreateReportDialog from "./CreateReportDialog";
import download from "downloadjs";
import AreYouSure from "../../molecules/AreYouSure/AreYouSure";
import { useAuthState } from "../../context/AuthContext";
import { isAdmin } from "../../utilities/checkUserRole";
import { IReport } from "../../network/models/Interfaces";
import { usePharmacyEnabledStatus } from "../../context/PharmacyEnabledStatusContext";
import PrintHeaderInfo from "../../organisms/PrintHeaderInfo/PrintHeaderInfo";
import { Tab, Tabs } from "@material-ui/core";
import { TabContext, TabPanel } from "@material-ui/lab";
import { ReportTypeOptions } from "../../utilities/SelectOptions";
import { usePharmacyInfo } from "../../hooks/usePharmacyInfo";
import EditReportDialog from "./EditReportDialog";
import AppBreadcrumbs from "../../organisms/Breadcrumbs/Breadcrumbs";
import { useStyles } from "./ReportsPageStyles";

export interface ReportsPageProps {}

export const ReportsPage: React.FC<ReportsPageProps> = (
  props: ReportsPageProps
) => {
  const { user } = useAuthState();
  const userRole = user!.role;
  const { getMethod, deleteMethod } = useApiService();
  const queryClient = useQueryClient();
  const params: any = useParams();
  const { pharmacyId, requestId } = params;
  const loaderDispatch = useLoaderDispatch();
  const { loading } = useLoaderState();
  const classes = useStyles({});
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const basePath = isAdminRoute ? "/admin/pharmacies" : "/pharmacies";
  
  const [openCreateReport, setOpenCreateReport] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [reportToEdit, setReportToEdit] = useState<IReport | undefined>();
  const [currentReport, setCurrentReport] =
    useState<{ id: number; originalFileName: string } | undefined>(undefined);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [toBeDeletedObject, setToBeDeletedObject] = useState({});
  const [tabSelected, setTabSelected] = useState(ReportTypeOptions[0].value)
  const [pharmacyInfo] = usePharmacyInfo(params.pharmacyId);

  const { pharmacyEnabledStatusMap } = usePharmacyEnabledStatus();
  const isReportIdDefined = useRef(false);

  const listReportsApi = async () => {
    let response;
    startLoading(loaderDispatch);
    response = await getMethod(
      `/pharmacies/${pharmacyId}/returnrequests/${requestId}/reports`
    );
    stopLoading(loaderDispatch);
    const data: Array<IReport> = response.data;
    return data;
  };
  const { data: reports } = useQuery("reports", listReportsApi);

const downloadReportApi = async ({ id }: { id: number }) => {
  startLoading(loaderDispatch);

  const response = await getMethod(
    `/pharmacies/${pharmacyId}/returnrequests/${requestId}/reports/${id}`,
    {
      responseType: "blob",
    },
  );

  stopLoading(loaderDispatch);

  const contentDisposition = response.headers["content-disposition"];

  let fileName = "report.pdf";

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match?.[1]) {
      fileName = match[1];
    }
  }

  return {
    fetchedFile: response.data,
    fileName,
  };
};


  const { refetch: downloadFile } = useQuery(
    ["download-file", currentReport],
    () => downloadReportApi(currentReport!),
    {
      enabled: false,
      onSuccess: ({ fetchedFile, fileName }) => {
        download(fetchedFile, fileName, "application/pdf");
      },
    },
  );

  const deleteReportApi = async ({ id }: any) => {
    startLoading(loaderDispatch);
    await deleteMethod(
      `/admin/pharmacies/${pharmacyId}/returnrequests/${requestId}/reports/${id}`
    );
    stopLoading(loaderDispatch);
    return id;
  };
  const { mutate: deleteReport } = useMutation(deleteReportApi, {
    onSuccess: (id) => {
      const newReports = [...reports!];
      newReports.forEach((item, i) => {
        if (item.id === id) {
          newReports.splice(i, 1);
          return;
        }
      });
      queryClient.setQueryData("reports", newReports);
    },
  });

  useEffect(() => {
    if (isReportIdDefined.current) {
      downloadFile();
    } else {
      isReportIdDefined.current = true;
    }
  }, [currentReport]);

  const getHeaderTitles = () => {
    let headerTitles = ["Description"];
    if (isAdmin(userRole)) {
      headerTitles.push("Wholesaler");
    }
    headerTitles.push("File");
    if (isAdmin(userRole)) {
      headerTitles.push("");
      headerTitles.push("");
    }
    return headerTitles;
  };

  const renderCreateReportButton = () => {
    if (isAdmin(userRole)) {
      return (
        <div style={{ marginTop: 0, marginBottom: 40 }}>
          <CallToActionButton
            height={"80px"}
            text="Create New Report"
            width={largeButton.width}
            onClick={() => {
              setOpenCreateReport(true);
            }}
            disabled={!pharmacyEnabledStatusMap[params.pharmacyId]}
          />
        </div>
      );
    }
  };

  const renderCreateReportDialog = () => {
    if (openCreateReport) {
      return (
        <CreateReportDialog
          open={openCreateReport}
          onClose={() => setOpenCreateReport(false)}
        />
      );
    }
  };

  const renderPharmacyInfo = () => {
    if (pharmacyInfo) {
      return (
        <PrintHeaderInfo pharmacyName={pharmacyInfo.pharmacy.doingBusinessAs} />
      );
    }
    return null;
  };


  const renderConfirmDeleteDialog = () => {
    if (openConfirmDelete) {
      return (
        <AreYouSure
          open={openConfirmDelete}
          onClose={() => {
            setOpenConfirmDelete(false);
          }}
          deleteObject={deleteReport}
          toBeDeletedObject={toBeDeletedObject}
        />
      );
    }
  };

const pharmacyName = pharmacyInfo?.pharmacy?.doingBusinessAs || "Pharmacy";

const breadcrumbItems = [
  {
    label: pharmacyName,
    path: `${basePath}/${pharmacyId}`,
  },
  {
    label: "Pharmacy Returns",
    path: `${basePath}/${pharmacyId}/returns`,
  },
  {
    label: "Reports",
  },
];

  return loading ? (
    <Loader />
  ) : (
    <div>
      {renderPharmacyInfo()}
      <div className={classes.breadcrumbsContainer}>
        <AppBreadcrumbs items={breadcrumbItems} />
      </div>
      {renderCreateReportButton()}
      <TabContext value={tabSelected}>
        <Tabs
          value={tabSelected}
          indicatorColor="primary"
          textColor="primary"
          onChange={(event, value) => setTabSelected(value)}
          aria-label="simple tabs example"
        >
          {ReportTypeOptions.map(({ label, value }) => (
            <Tab label={label} value={value} />
          ))}
        </Tabs>
        {ReportTypeOptions.map(({ label, value }) => (
          <TabPanel value={value}>
            <TableOrganism
              header={createHeaderRow(getHeaderTitles())}
              rows={createReportsRows(
                reports?.filter((report) => report.reportType === value),
                userRole,
                setOpenConfirmDelete,
                setToBeDeletedObject,
                setCurrentReport,
                setOpenEditDialog,
                setReportToEdit,
              )}
            />
          </TabPanel>
        ))}
        {openEditDialog && reportToEdit && (
          <EditReportDialog
            open={openEditDialog}
            report={reportToEdit}
            onClose={() => setOpenEditDialog(false)}
          />
        )}
      </TabContext>
      {renderCreateReportDialog()}
      {renderConfirmDeleteDialog()}
    </div>
  );
};

export default ReportsPage;
